import React, { useMemo, useState } from "react";
import { Typography } from "@mui/material";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import FulfillmentGrid from "../components/Shipping/FulfillmentGrid.jsx";
import PullOrdersSplitButton from "../components/Helpers/PullOrdersSplitButton.jsx";
import PostData from "../services/PostData.js";
import "../components/Products/catalog.css";

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultPullRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 3);
    return { startDate: dayInputValue(start), endDate: dayInputValue(end) };
}

function pullRangeError(startDate, endDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        return "Choose a start and end date.";
    }
    if (startDate > endDate) return "Start date must be on or before the end date.";
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const days = Math.round((end.getTime() - start.getTime()) / 86400000);
    if (days > 62) return "Date range cannot be longer than 62 days.";
    return "";
}

export default function Fulfillment() {
    const initialRange = useMemo(() => defaultPullRange(), []);
    const [pulling, setPulling] = useState(false);
    const [pullError, setPullError] = useState("");
    const [pullMessage, setPullMessage] = useState("");
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const [refreshKey, setRefreshKey] = useState(0);
    const rangeError = pullRangeError(startDate, endDate);

    const pullOrders = async (action) => {
        if (rangeError) return;
        setPulling(true);
        setPullError("");
        setPullMessage("");
        try {
            const response = await PostData(
                { action, startDate, endDate, syncStatuses: true },
                "broylesllc/shipping/get-orders",
                null
            );
            if (!response?.success) {
                const message = response?.error?.response?.data?.message;
                setPullError(message || "Could not pull orders for that date range.");
                return;
            }
            const summary = response?.data || {};
            const parts = [];
            if (summary.ebay != null) {
                parts.push(
                    summary.ebay === "failed" ? "eBay failed" : `${summary.ebay} eBay`
                );
            }
            if (summary.walmart != null) {
                parts.push(
                    summary.walmart === "failed"
                        ? "Walmart failed"
                        : `${summary.walmart} Walmart`
                );
            }
            if (summary.shopify != null) {
                parts.push(
                    summary.shopify === "failed"
                        ? "Shopify failed"
                        : `${summary.shopify} Shopify`
                );
            }
            const warningText = Array.isArray(summary.warnings) && summary.warnings.length
                ? ` ${summary.warnings.join("; ")}.`
                : "";
            setPullMessage(
                parts.length
                    ? `Pulled ${parts.join(", ")} for ${startDate}–${endDate} (ET).${warningText}`
                    : `Pull finished for ${startDate}–${endDate} (ET).${warningText}`
            );
            setRefreshKey((key) => key + 1);
        } finally {
            setPulling(false);
        }
    };

    return (
        <CatalogPageShell
            title="Fulfillment"
            subtitle="Pull orders, pick assigned units from the shelf, mark them picked, then finish labels in Shipping."
        >
            <div className="app-page-stack">
                <div className="catalog-toolbar" style={{ borderBottom: "none" }}>
                    <div className="catalog-toolbar-spacer" />
                    {pullError ? (
                        <Typography component="span" sx={{ color: "#b91c1c", fontSize: 13 }}>
                            {pullError}
                        </Typography>
                    ) : pullMessage ? (
                        <Typography component="span" sx={{ color: "#0f766e", fontSize: 13 }}>
                            {pullMessage}
                        </Typography>
                    ) : (
                        <span className="catalog-selection-hint">
                            Manage new orders here before Shipping
                        </span>
                    )}
                    <PullOrdersSplitButton
                        pulling={pulling}
                        onPull={pullOrders}
                        startDate={startDate}
                        endDate={endDate}
                        onStartChange={setStartDate}
                        onEndChange={setEndDate}
                        rangeError={rangeError}
                        hint="Pulls orders, assigns oldest Listed units, then refreshes this list."
                    />
                </div>
                <FulfillmentGrid pulling={pulling} refreshKey={refreshKey} />
            </div>
        </CatalogPageShell>
    );
}
